import classNames from "classnames";
import * as React from "react";
import { Accolades } from "../Accolades";
import "./PressReleases.scss";

interface IPressReleaseExpanderProps {
  title: string | JSX.Element;
}

export const PressReleaseExpander: React.FunctionComponent<IPressReleaseExpanderProps> =
  (props) => {
    const [expanded, setExpanded] = React.useState(false);
    return (
      <div className={classNames("PressReleaseExpander", { expanded })}>
        <div className="HeaderBar" onClick={() => setExpanded(!expanded)}>
          <span>{props.title}</span>
          <div className={classNames("Expander", { expanded })} />
        </div>
        <div className={classNames("Body", { expanded })}>{props.children}</div>
      </div>
    );
  };

interface IPressReleasesProps {}

export const PressReleases: React.FunctionComponent<IPressReleasesProps> = (
  props
) => {
  return (
    <div className={classNames("PressReleases")}>
      <div className="Expanders">
      <PressReleaseExpander title={<span>John Deere Reveals Fully Autonomous Tractor at CES<sup>®</sup> 2022</span>}>
          <h2>John Deere Reveals Fully Autonomous Tractor at CES<sup>®</sup> 2022</h2>

          <p>
            MOLINE, IL (January 4, 2022) – During a press conference at CES 2022, John Deere revealed a fully autonomous tractor that’s ready for large-scale production. The machine combines Deere’s 8R Tractor, TruSet™-enabled chisel plow, GPS guidance system, and new advanced technologies. The autonomous tractor will be available to farmers later this year.
          </p>

          <p>
            The autonomous tractor serves a specific purpose: feeding the world. The global population is {" "}
            <a href="https://globalagriculturalproductivity.org/wp-content/uploads/2019/01/2020-GAP_Report_Exec-Summary-1.pdf"  target="_blank" rel="noreferrer">
              expected
            </a>{" "}
            to grow from about 8 billion to nearly 10 billion people by 2050, increasing the global food demand by 50%. Furthermore, farmers must feed this growing population with less available land and skilled labor, and work through the variables inherent in farming like changing weather conditions and climate, variations in soil quality, and the presence of weeds and pests. All of these factors impact a farmer’s ability to farm during the most critical times of the year.
          </p>

          <p>
          The autonomous tractor has six pairs of stereo cameras, which enables 360-degree obstacle detection and the calculation of distance. Images captured by the cameras are passed through a deep neural network that classifies each pixel in approximately 100 milliseconds and determines if the machine continues to move or stops, depending on if an obstacle is detected. The autonomous tractor is also continuously checking its position relative to a geofence, ensuring it is operating where it is supposed to, and is accurate to within less than 1 inch.
          </p>

          <p>
          To use the autonomous tractor, farmers only need to transport the machine to a field and configure it for autonomous operation. Using John Deere Operations Center Mobile, they can swipe from left to right to start the machine. While the machine is working, the farmer can leave the field to focus on other tasks while monitoring the machine’s status from their mobile device.
          </p>

          <p>
            John Deere Operations Center Mobile provides access to live video, images, data, and metrics, and allows a farmer to adjust speed, depth, and more. In the event of any job-quality anomalies or machine-health issues, farmers will be notified remotely and can make adjustments to optimize the performance of the machine.
          </p>

          <p>
            The full press conference can be viewed on John Deere’s CES
            microsite: <a href="http://www.JohnDeere.com/CES"  target="_blank" rel="noreferrer">www.JohnDeere.com/CES</a>.
          </p>

          <h3>John Deere CES 2022 Booth, Panel, and Tireside Chats</h3>

          <p>
          The fully autonomous tractor will be on display from January 5–8, 2022, at John Deere’s CES booth, located in Central Plaza at the Las Vegas Convention Center (
            <a href="https://ces22.mapyourshow.com/8_0/floorplan/?hallID=L&selectedBooth=booth~CP-14"  target="_blank" rel="noreferrer">
              CP-14
            </a>
            ). The booth will also showcase cutting-edge technologies that are being used by farmers today, such as AI, GPS, automation, and more.
          </p>

          <p>
            John Deere will participate in three panel discussions during CES, focused on AI, 5G, and autonomy:
          </p>
          <ul>
            <li>On Wednesday, January 5 at 9:00 a.m. PT, John Deere’s Chief Technology Officer Jahmy Hindman will speak on a panel focused on exploring AI’s potential in business. Titled <a href="https://www.ces.tech/conference/Session/878940.aspx" target="_blank" rel="noreferrer">Frontiers in Business AI</a>, the panel will take place in the Las Vegas Convention Center’s North Hall in Room N251.</li>
            <li>On Wednesday, January 5 at 3:00 p.m. PT, Bear Flag Robotics’ Co-Founder and COO Aubrey Donnellan will speak on a WIRED panel focused on the path to full autonomy. Titled <a href="https://www.wired.com/hq/p/1" target="_blank" rel="noreferrer">The Long Road to Fully Autonomous Driving</a>, the panel will be streamed live on WIRED.com.</li>
            <li>On Thursday, January 6 at 10:20 a.m. PT, John Deere’s Vice President, Production & Precision Ag Production Systems Deanna Kovar will speak on a panel about how 5G will transform tech innovation. Titled <a href="https://ces.tech/conference/Session/880654.aspx" target="_blank" rel="noreferrer">Seizing the Opportunity of Tech Innovation with 5G</a>, the panel will take place in the Las Vegas Convention Center’s North Hall in Room N262.</li>
          </ul>

          <p>
            Additionally, Deere will hold a series of Tireside Chats at its booth, exploring key technologies and how they impact agriculture:
          </p>
          <ul>
            <li>
              Wednesday, January 5 from 3:30–3:45 p.m. PT: Driving
              (Autonomously) With Purpose
            </li>
            <li>
              Thursday, January 6 from 11:00–11:15 a.m. PT: This Isn’t Old
              MacDonald’s Farm
            </li>
            <li>
              Thursday, January 6 from 3:00–3:15 p.m. PT: Swipe To Begin Farming
            </li>
          </ul>

          <p>
            All of the content and demos from Deere’s CES booth will also be
            available on the company’s microsite:{" "}
            <a href="http://www.JohnDeere.com/CES" target="_blank" rel="noreferrer">www.JohnDeere.com/CES</a>.
          </p>

          <h3>About Deere &amp; Company</h3>
          <p>
            Deere &amp; Company (
            <a href="http://www.JohnDeere.com" target="_blank" rel="noreferrer">www.JohnDeere.com</a>) is a world
            leader in providing advanced products, technology, and services for
            customers whose work is revolutionizing agriculture and construction
            — those who cultivate, harvest, transform, enrich, and build upon
            the land to meet the world’s increasing need for food, fuel,
            shelter, and infrastructure.
          </p>

          <p>
            <strong>Contact:</strong> <br />
            Laurel Caes <br />
            Public Relations Manager – Technology &amp; Innovation <br />
            CaesLaurelJ@JohnDeere.com
          </p>
        </PressReleaseExpander>
        <PressReleaseExpander title={<span>John Deere Honored for Innovation at CES<sup>®</sup> 2022</span>}>
          <h2>
            John Deere Selected as CES<sup>®</sup> 2022 Innovation Awards Best
            of Innovation Honoree in Robotics Category and Honoree in Vehicle
            Intelligence &amp; Transportation Category
          </h2>
          <p>
            MOLINE, Illinois (November 10, 2021) — John Deere
            (NYSE: DE) has been named a CES<sup>®</sup> 2022 Innovation Awards Best of
            Innovation honoree in the Robotics category and an honoree in the
            Vehicle Intelligence &amp; Transportation category. This is the
            third consecutive year John Deere has received an Innovation Award
            from the Consumer Technology Association (CTA), which recognizes
            outstanding design and engineering in consumer technology products.
            This announcement was made ahead of CES 2022, the world’s most
            influential technology event, taking place January 5–8 in Las Vegas,
            Nevada, and digitally.
          </p>

          <p>
            John Deere is an Innovation Award honoree in two categories for its
            See &amp; Spray™ technology. See &amp; Spray™ is a technologically
            advanced robot for the agriculture industry that leverages computer
            vision and machine learning to detect the difference between plants
            and weeds and target application of herbicide on the weeds. This
            plant-level management technology gives a machine the gift of
            vision, benefiting the farmer, the surrounding community, and the
            environment.
          </p>

          <p>
            "The opportunity for technology in agriculture continues to inspire
            us to develop cutting-edge smart machines, systems, and solutions to
            treat each plant individually and support our customers," said Jahmy
            Hindman, chief technology officer for John Deere. "John Deere is at
            the forefront of innovation, and with technology like See &amp;
            Spray™, we’re helping our customers maximize sustainability,
            profitability, productivity, and visibility so they can focus on the
            important work of feeding a growing world population."
          </p>

          <p>
            See &amp; Spray™ uses deep learning, cameras, and robotics to
            distinguish plants from weeds. The future of this technology is key
            to treating each plant individually, providing only and exactly what
            every single plant needs at the moment, making chemical usage more
            efficient, protecting the surrounding soil and plants, and producing
            healthier, more productive crops.
          </p>

          <p>
            The Robotics category of the Innovation Awards highlights
            programmable or otherwise intelligent machines capable of performing
            specific tasks or replicating human movement or interactions. The
            Vehicle Intelligence &amp; Transportation category highlights
            automotive and other transportation products and services that
            integrate technology into the driving or riding experience. Products
            are reviewed and scored by a panel of judges comprised of designers,
            engineers, and technology media. Products are also evaluated on
            their engineering and functionality, aesthetics, and design; and
            what makes the product unique and innovative. Those with the highest
            rating receive the "Best of Innovation" distinction.
          </p>

          <p>
            The CES Innovation Awards are based upon descriptive materials
            submitted to the judges. CTA did not verify the accuracy of any
            submission or of any claims made and did not test the item to which
            the award was given.
          </p>
          <h4>About Deere &amp; Company</h4>
          <p>
            Deere &amp; Company (
            <a href="http://johndeere.com">www.JohnDeere.com</a>) is a world
            leader in providing advanced products, technology, and services for
            customers whose work is revolutionizing agriculture and construction
            — those who cultivate, harvest, transform, enrich, and build upon the
            land to meet the world’s increasing need for food, fuel, shelter, and
            infrastructure.
          </p>
          <Accolades title="" />
        </PressReleaseExpander>

      </div>
    </div>
  );
};
